import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Sparkles, MessageCircle, AlertTriangle, CheckCircle2, Lightbulb, BookOpenCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import TTSPlayer from "@/components/TTSPlayer";

export default function TopicPage() {
  const { subjectKey, topicCode } = useParams();
  const [topic, setTopic] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [loadingExp, setLoadingExp] = useState(false);

  useEffect(() => {
    api.get(`/curriculum/topics/${subjectKey}/${topicCode}`).then((r) => setTopic(r.data));
  }, [subjectKey, topicCode]);

  const loadExplanation = async () => {
    setLoadingExp(true);
    try {
      const r = await api.post(`/ai/topic/${subjectKey}/${topicCode}/explanation`);
      setExplanation(r.data);
    } catch (e) {
      toast.error("Anlatım üretilemedi. Tekrar dener misin?");
    } finally {
      setLoadingExp(false);
    }
  };

  const markCompleted = async () => {
    await api.post("/progress/topic/complete", { topic_code: topicCode, subject_key: subjectKey, completed: true });
    toast.success("Konu tamamlandı olarak işaretlendi 🎉");
  };

  if (!topic) return <div className="text-slate-500">Yükleniyor…</div>;

  return (
    <div className="space-y-5 ela-fade-in" data-testid="topic-page">
      <Link to={`/dersler/${subjectKey}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> {topic.subject_name}
      </Link>

      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wider">{topic.unit_name}</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">{topic.name}</h1>
        <p className="text-slate-600 text-sm mt-2">{topic.kazanim}</p>
      </div>

      <Tabs defaultValue="anlatim" className="space-y-4">
        <TabsList className="bg-slate-100 rounded-xl">
          <TabsTrigger value="anlatim" data-testid="tab-anlatim">AI Anlatımı</TabsTrigger>
          <TabsTrigger value="quiz" data-testid="tab-quiz">Mini Quiz</TabsTrigger>
        </TabsList>

        <TabsContent value="anlatim" className="space-y-4">
          {!explanation && (
            <Card className="p-8 rounded-2xl border-slate-200 text-center">
              <Sparkles className="w-10 h-10 text-[#FF6B6B] mx-auto mb-3" />
              <h3 className="font-heading text-lg font-semibold text-slate-900">AI ile bu konuyu öğren</h3>
              <p className="text-sm text-slate-500 mt-1 mb-5">Claude Sonnet 4.5 sana özel anlatım hazırlasın.</p>
              <Button onClick={loadExplanation} disabled={loadingExp}
                data-testid="generate-explanation-btn"
                className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white rounded-xl">
                {loadingExp ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Hazırlanıyor…</>) : "Anlatımı Başlat"}
              </Button>
            </Card>
          )}

          {explanation && (
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="md:col-span-2 p-6 rounded-2xl border-slate-200">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <BookOpenCheck className="w-5 h-5 text-[#FF6B6B]" />
                    <h3 className="font-heading font-semibold text-slate-900">Konu Özeti</h3>
                  </div>
                  <TTSPlayer text={explanation.ozet} testid="topic-tts" />
                </div>
                <div className="prose prose-slate prose-sm max-w-none whitespace-pre-wrap text-slate-700">
                  {explanation.ozet}
                </div>

                {explanation.ornek_cozumlu && (
                  <div className="mt-5 p-4 rounded-xl bg-rose-50/40 border border-rose-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-[#FF6B6B]" />
                      <span className="text-sm font-semibold text-slate-900">Örnek Çözüm</span>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{explanation.ornek_cozumlu}</div>
                  </div>
                )}
              </Card>

              <div className="space-y-4">
                <Card className="p-5 rounded-2xl border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-slate-900">Önemli Noktalar</span>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {(explanation.onemli_noktalar || []).map((n, i) => (
                      <li key={i} className="flex gap-2"><span className="text-green-600">•</span>{n}</li>
                    ))}
                  </ul>
                </Card>

                <Card className="p-5 rounded-2xl border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-slate-900">Sık Yapılan Hatalar</span>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-700">
                    {(explanation.sik_yapilan_hatalar || []).map((n, i) => (
                      <li key={i} className="flex gap-2"><span className="text-amber-600">•</span>{n}</li>
                    ))}
                  </ul>
                </Card>

                <div className="flex flex-col gap-2">
                  <Link to={`/ogretmen?topic=${topicCode}`}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
                    data-testid="ask-teacher-link">
                    <MessageCircle className="w-4 h-4" /> AI Öğretmene Sor
                  </Link>
                  <Button onClick={markCompleted} variant="outline"
                    data-testid="mark-completed-btn"
                    className="rounded-xl">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Tamamlandı İşaretle
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="quiz">
          <MiniQuiz subjectKey={subjectKey} topicCode={topicCode} topic={topic} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MiniQuiz({ subjectKey, topicCode, topic }) {
  const [difficulty, setDifficulty] = useState("kolay");
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const load = async (diff) => {
    setLoading(true);
    setSubmitted(false);
    setAnswers({});
    setResult(null);
    setDifficulty(diff);
    try {
      const r = await api.post(`/ai/topic/${subjectKey}/${topicCode}/quiz?difficulty=${diff}&count=5`);
      setQuestions(r.data.sorular || []);
      setStartTime(Date.now());
    } catch {
      toast.error("Quiz oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    const payload = (questions || []).map((q, i) => ({
      soru: q.soru, secilen: answers[i] || null, dogru: q.dogru,
      dogru_mu: answers[i] === q.dogru,
    }));
    const duration = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
    try {
      const r = await api.post("/progress/quiz", {
        topic_code: topicCode, subject_key: subjectKey, difficulty,
        answers: payload, duration_seconds: duration,
      });
      setResult(r.data);
      setSubmitted(true);
    } catch {
      toast.error("Cevaplar gönderilemedi");
    }
  };

  if (!questions) {
    return (
      <Card className="p-8 rounded-2xl border-slate-200 text-center">
        <Sparkles className="w-10 h-10 text-[#FF6B6B] mx-auto mb-3" />
        <h3 className="font-heading text-lg font-semibold text-slate-900">Kendini sına</h3>
        <p className="text-sm text-slate-500 mt-1 mb-5">5 soruluk özgün mini quiz oluştur.</p>
        <div className="flex gap-2 justify-center">
          {["kolay", "orta", "zor"].map((d) => (
            <Button key={d} onClick={() => load(d)} disabled={loading}
              data-testid={`quiz-difficulty-${d}`}
              variant={d === "kolay" ? "default" : "outline"}
              className={d === "kolay" ? "bg-[#FF6B6B] hover:bg-[#FF5252] rounded-xl" : "rounded-xl"}>
              {loading && d === difficulty ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {d[0].toUpperCase()+d.slice(1)}
            </Button>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="mini-quiz">
      {questions.map((q, i) => (
        <Card key={i} className="p-5 rounded-2xl border-slate-200">
          <div className="text-xs text-slate-500 mb-1">Soru {i + 1} / {questions.length}</div>
          <div className="text-sm font-medium text-slate-900 whitespace-pre-wrap">{q.soru}</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {Object.entries(q.secenekler || {}).map(([k, v]) => {
              const selected = answers[i] === k;
              const isCorrect = submitted && k === q.dogru;
              const isWrong = submitted && selected && k !== q.dogru;
              return (
                <button key={k} type="button"
                  data-testid={`q-${i}-opt-${k}`}
                  onClick={() => !submitted && setAnswers({ ...answers, [i]: k })}
                  className={`text-left p-3 rounded-xl border text-sm transition ${
                    isCorrect ? "border-green-400 bg-green-50 text-green-900" :
                    isWrong ? "border-red-400 bg-red-50 text-red-900" :
                    selected ? "border-[#FF6B6B] bg-rose-50" : "border-slate-200 hover:border-slate-300"
                  }`}>
                  <span className="font-semibold mr-2">{k})</span>{v}
                </button>
              );
            })}
          </div>
          {submitted && q.aciklama && (
            <div className="mt-3 p-3 rounded-lg bg-slate-50 text-xs text-slate-700">
              <span className="font-semibold">Açıklama: </span>{q.aciklama}
            </div>
          )}
        </Card>
      ))}

      {!submitted ? (
        <Button onClick={submit}
          disabled={Object.keys(answers).length < questions.length}
          data-testid="quiz-submit-btn"
          className="w-full bg-[#FF6B6B] hover:bg-[#FF5252] rounded-xl h-12">
          Cevapları Gönder
        </Button>
      ) : (
        <Card className="p-5 rounded-2xl border-slate-200 bg-rose-50/30">
          <div className="text-center">
            <div className="font-display text-4xl font-bold text-slate-900">{result.score}</div>
            <div className="text-sm text-slate-500">/ 100 · {result.correct}/{result.total} doğru</div>
          </div>
          <div className="flex gap-2 justify-center mt-4">
            <Button onClick={() => load(difficulty)} variant="outline" className="rounded-xl" data-testid="quiz-retry-btn">Tekrar Dene</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
